import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useTheme } from '@mui/material/styles';
import chroma from 'chroma-js';
import { useTranslation } from 'react-i18next';
import AppsIcon from '@mui/icons-material/Apps';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useWindowSize } from 'react-use';
import BlockLayout from '../stories/layout-components/BlockLayout';
import * as MuiIcons from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import ShareIcon from '@mui/icons-material/Share';
import { useNavigate } from 'react-router-dom';
import OrganizationChart from './OrganizationChart';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';

const ResponsiveGridLayout = WidthProvider(Responsive);

const STYLE_OPTIONS = [
  { value: 'listA', label: 'Liste' },
  { value: 'listS', label: 'Liste simple' },
  { value: 'calendar', label: 'Calendrier' },
  { value: 'kanban', label: 'Kanban' },
  { value: 'table', label: 'Table' },
];

const BLOCK_TYPE_OPTIONS = [
  { value: 'content', label: 'Content' },
  { value: 'list', label: 'List' },
  { value: 'action', label: 'Action' },
  { value: 'component', label: 'Component' },
];

const MATCH_OPTIONS = [
  { value: 'id:id', label: 'id:id' },
  { value: 'targetId:targetId', label: 'targetId:targetId' },
  {
    value: 'targetProfileId:targetProfileId',
    label: 'targetProfileId:targetProfileId',
  },
  { value: 'dependencyId:dependencyId', label: 'dependencyId:dependencyId' },
];

const SECTION_OPTIONS = [
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'FINANCES', label: 'Finances' },
];

const BlueprintEditor = ({
  open,
  onClose,
  onSave,
  initialData,
  businessData,
  blueprintId,
  refreshData,
}) => {
  const [data, setData] = useState(
    initialData || {
      structures: [],
      modules: [],
      extensions: [],
    }
  );
  const theme = useTheme();
  const { t } = useTranslation();
  const printRef = useRef();
  const navigate = useNavigate();

  const [selectedStructure, setSelectedStructure] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [mainTab, setMainTab] = useState(0);
  const [rowHeight, setRowHeight] = useState(30);
  const [isDragging, setIsDragging] = useState(false);
  const [modalStructureNew, setModalStructureNew] = useState(false);
  const [newStructureName, setNewStructureName] = useState('');
  const [newStructureCollectionField, setNewStructureCollectionField] =
    useState('');
  const [selectedPathStructures, setSelectedPathStructures] = useState([]);
  const { height } = useWindowSize();
  const [selectedModule, setSelectedModule] = useState(null);
  const mainColor =
    businessData?.mainColor || theme.palette.primary.main || '#1604DD';
  const secColor =
    businessData?.secColor || theme.palette.secondary.main || '#1604DD';
  const isDarkMode = false;
  const isMobile = window.innerWidth < 768;
  const [showStructureSettings, setShowStructureSettings] = useState(false);
  const [filteredIcons, setFilteredIcons] = useState(
    Object.keys(MuiIcons).filter((iconName) => iconName.endsWith('Outlined'))
  );

  const gridContainerStyle = {
    display: 'grid',
    gridTemplateAreas: `
      "zone2 zone1 zone1"
      "zone2 zone3 zone4"
      "zone5 zone5 zone4"
    `,
    gridTemplateColumns: '2fr 2fr 1.5fr',
    gridTemplateRows: '120px 150px 150px',
    gap: 1,
    width: '100%',
    height: '440px',
  };

  useEffect(() => {
    if (open && initialData) {
      setData(initialData);
    }
  }, [open, initialData]);

  const createStructureData = async (collectionField, name) => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'master',
        body: {
          businessId: businessData.id,
          action: 'create-structure',
          data: {
            collectionField: collectionField,
            name: name,
          },
        },
      });

      return response?.structureData?.structureId;
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const lightGreyScale = chroma
    .scale([mainColor, '#f2f2f2'])
    .mode('lab')
    .colors(10)
    .map((color) => chroma(color).brighten(1.4));

  const lightGreyScaleSec = chroma
    .scale([secColor, '#f2f2f2'])
    .mode('lab')
    .colors(10)
    .map((color) => chroma(color).brighten(1.4));

  const darkColor = () => {
    let finalColor = mainColor || '#000000';

    return chroma(finalColor).darken(0.9).hex();
  };

  const handleLayoutChange = (layout, layouts) => {
    if (!selectedStructure || !selectedStructure.tabs) return;

    const updatedStructures = data.structures.map((structure) => {
      if (structure.id === selectedStructure.id) {
        const updatedTabs = structure.tabs.map((tab, index) => {
          if (index === selectedTab) {
            return {
              ...tab,
              blocks: layout.map((block) => {
                const existingBlock = tab.blocks.find((b) => b.i === block.i);
                return {
                  ...block,
                  x: block.x || 0,
                  y: block.y || 0,
                  w: block.w || 1,
                  h: block.h || 1,
                  i: block.i || `block-${Date.now()}`,
                  name: existingBlock?.name || block.name || `Block ${block.i}`,
                  name_fr:
                    existingBlock?.name_fr ||
                    block.name_fr ||
                    `Block ${block.i}`,
                  structureId: block.structureId || selectedStructure.id,
                  contentType: block.contentType || 'content',
                  color: block.color || 'white',
                  bgPattern: block.bgPattern || '',
                  match: block.match || '',
                  filter: block.filter || {},
                  createBtn: block.createBtn || false,
                  editBtn: block.editBtn || false,
                  refreshBtn: block.refreshBtn || false,
                  realtime: block.realtime || false,
                  icon: block.icon || '',
                  infos: block.infos || {},
                  groups: block.groups || [],
                  data: block.data || [],
                  price: block.price || 0,
                  minW: block.minW || 1,
                  minH: block.minH || 1,
                  maxW: block.maxW || 12,
                  maxH: block.maxH || 12,
                  static: block.static || false,
                  isDraggable:
                    block.isDraggable !== undefined ? block.isDraggable : true,
                  isResizable:
                    block.isResizable !== undefined ? block.isResizable : true,
                };
              }),
            };
          }
          return tab;
        });

        return {
          ...structure,
          tabs: updatedTabs,
        };
      }
      return structure;
    });

    setData({
      ...data,
      structures: updatedStructures,
    });
  };

  const handleUpdateBlock = (updatedBlock, blockId) => {
    if (!selectedStructure || !selectedStructure.tabs) return;
    const newData = {
      ...data,
      structures: data.structures.map((structure) => {
        if (structure.id === selectedStructure.id) {
          return {
            ...structure,
            tabs: structure.tabs.map((tab, tabIndex) => {
              if (tabIndex === selectedTab) {
                return {
                  ...tab,
                  blocks: tab.blocks.map((block) => {
                    if (block.i === blockId) {
                      const updatedBlockData = {
                        x: block.x || 0,
                        y: block.y || 0,
                        w: block.w || 1,
                        h: block.h || 1,
                        i: block.i || `block-${Date.now()}`,
                        minW: block.minW || 1,
                        minH: block.minH || 1,
                        maxW: block.maxW || 12,
                        maxH: block.maxH || 12,
                        static: block.static || false,
                        isDraggable:
                          block.isDraggable !== undefined
                            ? block.isDraggable
                            : true,
                        isResizable:
                          block.isResizable !== undefined
                            ? block.isResizable
                            : true,
                        name: block.name || `Block ${block.i}`,
                        name_fr: block.name_fr || `Block ${block.i}`,
                        name_en: block.name_en || `Block ${block.i}`,
                        structureId: block.structureId || selectedStructure.id,
                        contentType: block.contentType || 'content',
                        color: block.color || 'white',
                        bgPattern: block.bgPattern || '',
                        match: block.match || '',
                        filter: block.filter || {},
                        createBtn: block.createBtn || false,
                        editBtn: block.editBtn || false,
                        refreshBtn: block.refreshBtn || false,
                        realtime: block.realtime || false,
                        icon: block.icon || '',
                        infos: block.infos || {},
                        groups: block.groups || [],
                        data: block.data || [],
                        price: block.price || 0,
                        ...updatedBlock,
                      };
                      return updatedBlockData;
                    }
                    return block;
                  }),
                };
              }
              return tab;
            }),
          };
        }
        return structure;
      }),
    };

    setData(newData);

    const updatedStructure = newData.structures.find(
      (s) => s.id === selectedStructure.id
    );

    if (updatedStructure) {
      setSelectedStructure(updatedStructure);
    }
  };

  useEffect(() => {
    const availableHeight = height - 120;
    const desiredRows = 28;
    const calculatedHeight = Math.max(10, availableHeight / desiredRows);
    setRowHeight(calculatedHeight);
  }, [height]);

  const handleSave = () => {
    onSave(data);
  };

  const getStructureIcon = (structure) => {
    if (!structure?.icon) return <DashboardIcon />;
    const IconComponent = MuiIcons[structure.icon];
    return IconComponent ? <IconComponent /> : <DashboardIcon />;
  };

  const handleAddNewStructureSelect = () => {
    setModalStructureNew(true);
  };
  const handleAddNewStructure = async (collectionField, name) => {
    try {
      const newStruc = await createStructureData(collectionField, name);

      if (newStruc) {
        refreshData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddNewModule = () => {
    const newModule = {
      id: `module-${Date.now()}`,
      name: 'New Module',
      name_fr: 'Nouveau Module',
      name_en: 'New Module',
      description: '',
      icon: 'Apps',
      section: 'OPERATIONS',
      list: {
        tabs: [
          {
            listType: 'listA',
          },
        ],
        preferences: {
          limit: 10,
        },
      },
      price: 0,
    };

    setData({
      ...data,
      modules: [...data.modules, newModule],
    });
    setSelectedModule(newModule);
  };

  const handleStructureUpdate = (updatedFields) => {
    if (!selectedStructure) return;

    const updatedStructure = {
      ...selectedStructure,
      ...updatedFields,
    };

    const updatedStructures = data.structures.map((structure) =>
      structure.id === selectedStructure.id ? updatedStructure : structure
    );

    setData({
      ...data,
      structures: updatedStructures,
    });
    setSelectedStructure(updatedStructure);
  };

  const handleAddBlock = () => {
    if (!selectedStructure || !selectedStructure.tabs) return;

    const newBlock = {
      i: `block-${Date.now()}`,
      x: 0,
      y: 0,
      w: 4,
      h: 4,
      name: 'New Block',
      name_fr: 'Nouveau Bloc',
      name_en: 'New Block',
      contentType: 'content',
      color: 'white',
      bgPattern: '',
      match: '',
      filter: {},
      createBtn: false,
      editBtn: false,
      refreshBtn: false,
      realtime: false,
      icon: '',
      infos: {},
      groups: [],
      data: [],
      price: 0,
      minW: 1,
      minH: 1,
      maxW: 12,
      maxH: 12,
      static: false,
      isDraggable: true,
      isResizable: true,
    };

    const updatedStructures = data.structures.map((structure) => {
      if (structure.id === selectedStructure.id) {
        const updatedTabs = structure.tabs.map((tab, index) => {
          if (index === selectedTab) {
            const currentBlocks = tab.blocks || [];
            return {
              ...tab,
              blocks: [...currentBlocks, newBlock],
            };
          }
          return tab;
        });

        return {
          ...structure,
          tabs: updatedTabs,
        };
      }
      return structure;
    });

    setData({
      ...data,
      structures: updatedStructures,
    });

    const currentTab = selectedStructure.tabs[selectedTab];
    if (currentTab) {
      const updatedTab = {
        ...currentTab,
        blocks: [...(currentTab.blocks || []), newBlock],
      };
      const updatedStructure = {
        ...selectedStructure,
        tabs: selectedStructure.tabs.map((tab, index) =>
          index === selectedTab ? updatedTab : tab
        ),
      };
      setSelectedStructure(updatedStructure);
    }
  };

  const handleRemoveBlock = (blockId) => {
    if (!selectedStructure || !selectedStructure.tabs) return;

    const updatedStructures = data.structures.map((structure) => {
      if (structure.id === selectedStructure.id) {
        const updatedTabs = structure.tabs.map((tab, index) => {
          if (index === selectedTab) {
            return {
              ...tab,
              blocks: tab.blocks.filter((block) => block.i !== blockId),
            };
          }
          return tab;
        });

        return {
          ...structure,
          tabs: updatedTabs,
        };
      }
      return structure;
    });

    setData({
      ...data,
      structures: updatedStructures,
    });
  };

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}/blueprint/${blueprintId}`;
      await navigator.clipboard.writeText(shareUrl);
      alert('Shareable link copied to clipboard!');
    } catch (error) {
      console.error('Error sharing blueprint:', error);
      alert('Failed to generate shareable link. Please try again.');
    }
  };

  const handleAddPath = () => {
    if (!selectedPathStructures.length || !selectedStructure) return;

    // Add the current structure to the path if it's not already the last part
    const currentStructureId = selectedStructure.id;
    if (
      selectedPathStructures[selectedPathStructures.length - 1] !==
      currentStructureId
    ) {
      selectedPathStructures.push(currentStructureId);
    }

    const pathString = selectedPathStructures.join('/');

    const updatedPaths = [...(selectedStructure.paths || []), pathString];
    handleStructureUpdate({ paths: updatedPaths });
    setSelectedPathStructures([]);
  };

  const handleRemovePath = (pathToRemove) => {
    const updatedPaths = (selectedStructure.paths || []).filter(
      (path) => path !== pathToRemove
    );
    handleStructureUpdate({ paths: updatedPaths });
  };

  const handleAddStructureToPath = (structureId) => {
    if (!selectedPathStructures.includes(structureId)) {
      setSelectedPathStructures([...selectedPathStructures, structureId]);
    }
  };

  const handleRemoveStructureFromPath = (index) => {
    const updatedStructures = [...selectedPathStructures];
    updatedStructures.splice(index, 1);
    setSelectedPathStructures(updatedStructures);
  };

  const renderStructureSettings = () => {
    if (!selectedStructure) return null;

    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Structure Settings</Typography>
          <IconButton
            size="small"
            onClick={() => setShowStructureSettings(false)}
            sx={{
              height: '24px',
              width: '24px',
              maxHeight: '24px',
              maxWidth: '24px',
              minHeight: '24px',
              minWidth: '24px',
              padding: '3px',
            }}
          >
            <MuiIcons.Close />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} sx={{ width: '25%' }}>
            <TextField
              fullWidth
              label="Name (English)"
              value={selectedStructure.name_en || ''}
              onChange={(e) =>
                handleStructureUpdate({ name_en: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ width: '25%' }}>
            <TextField
              fullWidth
              label="Name (French)"
              value={selectedStructure.name_fr || ''}
              onChange={(e) =>
                handleStructureUpdate({ name_fr: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ width: '25%' }}>
            <FormControl fullWidth>
              <InputLabel>Collection Field</InputLabel>
              <Select
                value={selectedStructure.collectionField || ''}
                label="Collection Field"
                onChange={(e) =>
                  handleStructureUpdate({ collectionField: e.target.value })
                }
              >
                <MenuItem value="contacts">Contacts</MenuItem>
                <MenuItem value="profiles">Profiles</MenuItem>
                <MenuItem value="cardsinvoiced">Cards Invoiced</MenuItem>
                <MenuItem value="cardsuninvoiced">Cards Uninvoiced</MenuItem>
                <MenuItem value="tasks">Tasks</MenuItem>
                <MenuItem value="storages">Tasks</MenuItem>
                <MenuItem value="passes">Passes</MenuItem>
                <MenuItem value="services">Services</MenuItem>
                <MenuItem value="articles">Articles</MenuItem>
                <MenuItem value="grids">Grids</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} sx={{ width: '20%' }}>
            <TextField
              fullWidth
              label="Icon Name"
              value={selectedStructure.icon || ''}
              onChange={(e) => handleStructureUpdate({ icon: e.target.value })}
            />
          </Grid>

          {/* New Path Management Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Paths
            </Typography>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <FormControl fullWidth sx={{ width: '300px' }}>
                <InputLabel>Add Structure to Path</InputLabel>
                <Select
                  value=""
                  onChange={(e) => handleAddStructureToPath(e.target.value)}
                  label="Add Structure to Path"
                  sx={{ width: '300px' }}
                >
                  {data.structures
                    .filter(
                      (structure) =>
                        !selectedPathStructures.includes(structure.id)
                    )
                    .map((structure) => (
                      <MenuItem key={structure.id} value={structure.id}>
                        {structure.name_fr ||
                          structure.name_en ||
                          structure.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Box
                className="d-flex align-left"
                sx={{ width: '480px', marginRight: '50px' }}
              >
                {selectedPathStructures.map((structureId, index) => {
                  const structure = data.structures.find(
                    (s) => s.id === structureId
                  );
                  return (
                    <div key={index}>
                      /
                      {structure.name_fr || structure.name_en || structure.name}
                    </div>
                  );
                })}
              </Box>

              <Button
                variant="contained"
                onClick={handleAddPath}
                disabled={!selectedPathStructures.length}
                startIcon={<MuiIcons.Add />}
                sx={{ width: '200px', height: '40px' }}
                size="small"
              >
                Add Path
              </Button>
            </Box>

            <List>
              {(selectedStructure.paths || []).map((path, index) => {
                const structureIds = path.split('/');
                const pathNames = structureIds
                  .map((id) => {
                    const structure = data.structures.find((s) => s.id === id);
                    return structure
                      ? structure.name_fr || structure.name_en || structure.name
                      : id;
                  })
                  .join(' / ');

                return (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemovePath(path)}
                      >
                        <MuiIcons.Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={pathNames} />
                  </ListItem>
                );
              })}
            </List>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderStructureSelector = () => (
    <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ ml: 2 }}>
          Structures
        </Typography>
        <Box>
          {selectedStructure && (
            <IconButton
              color="primary"
              size="small"
              onClick={() => setShowStructureSettings(true)}
              sx={{
                height: '24px',
                width: '24px',
                maxHeight: '24px',
                maxWidth: '24px',
                minHeight: '24px',
                minWidth: '24px',
                padding: '3px',
                mr: 1,
              }}
            >
              <MuiIcons.Settings />
            </IconButton>
          )}
          <IconButton
            color="primary"
            size="small"
            onClick={handleAddNewStructureSelect}
            sx={{
              height: '24px',
              width: '24px',
              maxHeight: '24px',
              maxWidth: '24px',
              minHeight: '24px',
              minWidth: '24px',
              padding: '3px',
            }}
          >
            <MuiIcons.Add />
          </IconButton>
        </Box>
      </Box>
      <List>
        {data?.structures?.map((structure) => (
          <ListItem
            key={structure.id}
            button
            dense
            divider
            selected={selectedStructure?.id === structure.id}
            onClick={() => {
              setSelectedStructure(structure);
              setSelectedTab(0);
              setShowStructureSettings(false);
            }}
          >
            <ListItemIcon>{getStructureIcon(structure)}</ListItemIcon>
            <ListItemText
              primary={structure.name_fr || structure.name_en || structure.name}
              secondary={structure.description}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  const renderStructureTabs = () => {
    if (!selectedStructure || !selectedStructure.tabs) return null;

    return (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
        >
          {selectedStructure.tabs.map((tab, index) => (
            <Tab key={index} label={tab.name_fr || tab.name_en || tab.name} />
          ))}
        </Tabs>
      </Box>
    );
  };

  const renderStructureContent = () => {
    if (!selectedStructure || !selectedStructure.tabs) return null;

    const currentTab = selectedStructure.tabs[selectedTab];
    if (!currentTab) return null;

    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2,
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 999,
            marginTop: '-20px',
          }}
        >
          <IconButton
            color="primary"
            size="small"
            onClick={handleAddBlock}
            sx={{
              height: '24px',
              width: '24px',
              maxHeight: '24px',
              maxWidth: '24px',
              minHeight: '24px',
              minWidth: '24px',
              padding: '3px',
            }}
          >
            <MuiIcons.Add /> Bloc
          </IconButton>
        </Box>
        <ResponsiveGridLayout
          className="layout"
          style={{
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
          layouts={{
            lg: currentTab.blocks?.map((block, index) => ({
              i: block?.i || index.toString(),
              x: block.x,
              y: block.y,
              w: block.w,
              h: block.h,
              structureId: block.structureId || selectedStructure.id,
              contentType: block.contentType || 'content',
              color: block.color || 'white',
              bgPattern: block.bgPattern || '',
              price: block.price || 0,
              match: block.match || '',
              filter: block.filter || {},
              createBtn: block.createBtn || false,
              editBtn: block.editBtn || false,
              refreshBtn: block.refreshBtn || false,
              realtime: block.realtime || false,
              icon: block.icon || '',
              infos: block.infos || {},
              groups: block.groups || [],
              data: block.data || [],
            })),
          }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={20}
          margin={[10, 10]}
          containerPadding={[8, 8]}
          onLayoutChange={handleLayoutChange}
          isDraggable
          isResizable
          useCSSTransforms={false}
        >
          {currentTab.blocks?.map((blockLayout, index) => (
            <div
              key={blockLayout?.i || index}
              style={{ width: '100%', height: '100%' }}
            >
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveBlock(blockLayout.i)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    zIndex: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    height: '24px',
                    width: '24px',
                    maxHeight: '24px',
                    maxWidth: '24px',
                    minHeight: '24px',
                    minWidth: '24px',
                    padding: '3px',
                  }}
                >
                  <MuiIcons.Close fontSize="small" />
                </IconButton>
                <BlockLayout
                  layout={{
                    ...blockLayout,
                    structureId:
                      blockLayout.structureId || selectedStructure.id,
                    contentType: blockLayout.contentType || 'content',
                    color: blockLayout.color || 'white',
                    bgPattern: blockLayout.bgPattern || '',
                    match: blockLayout.match || '',
                    name:
                      blockLayout.name ||
                      blockLayout.name_fr ||
                      `Block ${index + 1}`,
                    filter: blockLayout.filter || {},
                    createBtn: blockLayout.createBtn || false,
                    editBtn: blockLayout.editBtn || false,
                    refreshBtn: blockLayout.refreshBtn || false,
                    realtime: blockLayout.realtime || false,
                    icon: blockLayout.icon || '',
                    infos: blockLayout.infos || {},
                    price: blockLayout.price || 0,
                  }}
                  elementDetails={{
                    header: {
                      name:
                        blockLayout.name ||
                        blockLayout.name_fr ||
                        `Block ${index + 1}`,
                      filter: blockLayout.filter || {},
                      labels: true,
                      createBtn: blockLayout.createBtn || false,
                      editBtn: blockLayout.editBtn || false,
                      refreshBtn: blockLayout.refreshBtn || false,
                      realtime: blockLayout.realtime || false,
                      icon: blockLayout.icon || '',
                      infos: blockLayout.infos || {},
                      changesInProgress: [],
                    },
                    groups: blockLayout.groups || [],
                    data: blockLayout.data || [],
                  }}
                  childrenComponent="content"
                  editMode={true}
                  publicMode={false}
                  activeIndex={index}
                  setDrag={setIsDragging}
                  handleUpdateBlock={handleUpdateBlock}
                  structureId={selectedStructure.id}
                  moduleName=""
                  heightPercentage={(blockLayout.h * rowHeight) / 12}
                  noPadding={true}
                  fromList={true}
                  handleRefresh={() => {}}
                  handleDelete={() => {}}
                  blockTypeOptions={BLOCK_TYPE_OPTIONS}
                  matchOptions={MATCH_OPTIONS}
                  structures={data.structures}
                />
              </Box>
            </div>
          ))}
        </ResponsiveGridLayout>
      </Box>
    );
  };

  const renderModuleSelector = () => (
    <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ ml: 2 }}>
          Modules
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{
            height: '24px',
            width: '24px',
            maxHeight: '24px',
            maxWidth: '24px',
            minHeight: '24px',
            minWidth: '24px',
            padding: '3px',
          }}
          onClick={handleAddNewModule}
        >
          <MuiIcons.Add />
        </Button>
      </Box>
      <List>
        {data?.modules?.map((module) => (
          <ListItem
            key={module.id}
            button
            dense
            divider
            selected={selectedModule?.id === module.id}
            onClick={() => setSelectedModule(module)}
          >
            <ListItemIcon>{getStructureIcon(module)}</ListItemIcon>
            <ListItemText
              primary={module.name_fr || module.name_en || module.name}
              secondary={module.description}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  const renderModuleOptions = () => {
    if (!selectedModule) return null;

    return (
      <Paper elevation={3} sx={{ p: 3, mt: 1, height: '100%' }}>
        <Typography variant="h6" gutterBottom>
          Module -{' '}
          {selectedModule.name_fr ||
            selectedModule.name_en ||
            selectedModule.name}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Name</InputLabel>
              <TextField
                value={selectedModule.name || ''}
                onChange={(e) => {
                  const updatedModule = {
                    ...selectedModule,
                    name: e.target.value,
                  };
                  const updatedData = {
                    ...data,
                    modules: data.modules.map((module) =>
                      module.id === selectedModule.id ? updatedModule : module
                    ),
                  };
                  setData(updatedData);
                  setSelectedModule(updatedModule);
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Name (French)</InputLabel>
              <TextField
                value={selectedModule.name_fr || ''}
                onChange={(e) => {
                  const updatedModule = {
                    ...selectedModule,
                    name_fr: e.target.value,
                  };
                  const updatedData = {
                    ...data,
                    modules: data.modules.map((module) =>
                      module.id === selectedModule.id ? updatedModule : module
                    ),
                  };
                  setData(updatedData);
                  setSelectedModule(updatedModule);
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Name (English)</InputLabel>
              <TextField
                value={selectedModule.name_en || ''}
                onChange={(e) => {
                  const updatedModule = {
                    ...selectedModule,
                    name_en: e.target.value,
                  };
                  const updatedData = {
                    ...data,
                    modules: data.modules.map((module) =>
                      module.id === selectedModule.id ? updatedModule : module
                    ),
                  };
                  setData(updatedData);
                  setSelectedModule(updatedModule);
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Section</InputLabel>
              <Select
                value={selectedModule.section || ''}
                label="Section"
                onChange={(e) => {
                  const updatedModule = {
                    ...selectedModule,
                    section: e.target.value,
                  };
                  const updatedData = {
                    ...data,
                    modules: data.modules.map((module) =>
                      module.id === selectedModule.id ? updatedModule : module
                    ),
                  };
                  setData(updatedData);
                  setSelectedModule(updatedModule);
                }}
              >
                {SECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Style</InputLabel>
              <Select
                value={selectedModule.list?.tabs?.[0]?.listType || ''}
                label="Style"
                onChange={(e) => {
                  const updatedModule = {
                    ...selectedModule,
                    list: {
                      ...selectedModule.list,
                      tabs: [
                        {
                          ...selectedModule.list.tabs[0],
                          listType: e.target.value,
                        },
                      ],
                    },
                  };
                  const updatedData = {
                    ...data,
                    modules: data.modules.map((module) =>
                      module.id === selectedModule.id ? updatedModule : module
                    ),
                  };
                  setData(updatedData);
                  setSelectedModule(updatedModule);
                }}
              >
                {STYLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Structure</InputLabel>
              <Select
                value={selectedModule?.structureId || ''}
                label="Structure"
                onChange={(e) => {
                  const updatedModule = {
                    ...selectedModule,
                    structureId: e.target.value,
                  };
                  const updatedData = {
                    ...data,
                    modules: data.modules.map((module) =>
                      module.id === selectedModule.id ? updatedModule : module
                    ),
                  };
                  setData(updatedData);
                  setSelectedModule(updatedModule);
                }}
              >
                <MenuItem value="">None</MenuItem>
                {data.structures.map((structure) => (
                  <MenuItem key={structure.id} value={structure.id}>
                    {structure.name_fr || structure.name_en || structure.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Limit</InputLabel>
              <TextField
                type="number"
                value={selectedModule.list?.preferences?.limit || ''}
                onChange={(e) => {
                  const updatedModule = {
                    ...selectedModule,
                    list: {
                      ...selectedModule.list,
                      preferences: {
                        ...selectedModule.list?.preferences,
                        limit: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      },
                    },
                  };
                  const updatedData = {
                    ...data,
                    modules: data.modules.map((module) =>
                      module.id === selectedModule.id ? updatedModule : module
                    ),
                  };
                  setData(updatedData);
                  setSelectedModule(updatedModule);
                }}
                inputProps={{ min: 1 }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Price</InputLabel>
              <TextField
                type="number"
                value={selectedModule.price || ''}
                onChange={(e) => {
                  const updatedModule = {
                    ...selectedModule,
                    price: e.target.value
                      ? parseFloat(e.target.value)
                      : undefined,
                  };
                  const updatedData = {
                    ...data,
                    modules: data.modules.map((module) =>
                      module.id === selectedModule.id ? updatedModule : module
                    ),
                  };
                  setData(updatedData);
                  setSelectedModule(updatedModule);
                }}
                inputProps={{
                  min: 0,
                  step: 0.01,
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '50%' }}>
            <FormControl fullWidth>
              <InputLabel shrink>Icon</InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Select
                  value={selectedModule.icon || ''}
                  onChange={(e) => {
                    const updatedModule = {
                      ...selectedModule,
                      icon: e.target.value,
                    };
                    const updatedData = {
                      ...data,
                      modules: data.modules.map((module) =>
                        module.id === selectedModule.id ? updatedModule : module
                      ),
                    };
                    setData(updatedData);
                    setSelectedModule(updatedModule);
                  }}
                  sx={{ flex: 1 }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {filteredIcons.map((iconName) => (
                    <MenuItem key={iconName} value={iconName}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {React.createElement(MuiIcons[iconName], {
                          sx: { fontSize: 20 },
                        })}
                        <Typography variant="body2">{iconName}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {selectedModule.icon && MuiIcons[selectedModule.icon] && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {React.createElement(MuiIcons[selectedModule.icon], {
                      sx: { fontSize: 24 },
                    })}
                  </Box>
                )}
              </Box>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderRecapTab = () => {
    const blockPrices = data.structures.reduce((acc, structure) => {
      if (!structure.tabs) return acc;
      return (
        acc +
        structure.tabs.reduce((tabAcc, tab) => {
          if (!tab.blocks) return tabAcc;
          return (
            tabAcc +
            tab.blocks.reduce((blockAcc, block) => {
              return (
                blockAcc + (block.price !== undefined ? Number(block.price) : 0)
              );
            }, 0)
          );
        }, 0)
      );
    }, 0);

    const modulePrices = data.modules.reduce((acc, module) => {
      return acc + (module.price !== undefined ? Number(module.price) : 0);
    }, 0);

    const totalPrice = blockPrices + modulePrices;

    return (
      <Box ref={printRef}>
        <Grid
          container
          spacing={3}
          sx={{ width: '100%', justifyContent: 'center' }}
        >
          <Grid item xs={12} sx={{ width: '650px' }}>
            <Paper elevation={3} sx={{ p: 3, height: '100%', width: '580px' }}>
              {/* Blocks Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: 'primary.main' }}
                >
                  Blocs
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {data.structures.map((structure) => (
                  <Box key={structure.id} sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      {structure.name_fr || structure.name_en || structure.name}
                    </Typography>
                    {(structure.tabs || []).map((tab, tabIndex) => (
                      <Box key={tabIndex} sx={{ pl: 2 }}>
                        {(tab.blocks || []).map((block) => (
                          <Box
                            key={block.i}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Typography>
                              {block.name_fr ||
                                block.name ||
                                `Block ${block.i}`}
                            </Typography>
                            <Typography>
                              {block.price
                                ? `${Number(block.price).toFixed(2)} $`
                                : '0.00 $'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                ))}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Total Blocs
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {blockPrices.toFixed(2)} $
                  </Typography>
                </Box>
              </Box>

              {/* Modules Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: 'primary.main' }}
                >
                  Modules
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {data.modules
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((module) => (
                    <Box
                      key={module.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography>
                        {module.name_fr || module.name_en || module.name}
                      </Typography>
                      <Typography>
                        {module.price
                          ? `${Number(module.price).toFixed(2)} $`
                          : '0.00 $'}
                      </Typography>
                    </Box>
                  ))}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Total Modules
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {modulePrices.toFixed(2)} $
                  </Typography>
                </Box>
              </Box>

              {/* Total Section */}
              <Box
                sx={{ mt: 4, pt: 2, borderTop: 2, borderColor: 'primary.main' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total Général
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: 'primary.main' }}
                  >
                    {totalPrice.toFixed(2)} $
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              width: '45%',
              height: '500px',
              maxHeight: '500px',
              minHeight: '500px',
            }}
          >
            <Paper elevation={3} sx={{ p: 3, height: '570px', width: '870px' }}>
              <div
                id="platform"
                className="align-left mt-2"
                style={{
                  position: 'sticky',
                  overflow: 'hidden',
                  padding: '15px',
                  borderRadius: '20px',
                  height: '500px',
                  maxHeight: '500px',
                  minHeight: '500px',
                  top: 0,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    zIndex: -1,
                  }}
                >
                  <div
                    style={{
                      width: '500px',
                      height: '300px',
                      overflow: 'hidden',
                      backgroundColor: lightGreyScale[0] + '60',
                      borderRadius: '50%',
                      filter: 'blur(120px)',
                      position: 'absolute',
                      top: '220px',
                      left: '-420px',
                    }}
                  />
                  <div
                    style={{
                      width: '800px',
                      height: '500px',
                      overflow: 'hidden',
                      backgroundColor: lightGreyScaleSec[2] + '50',
                      borderRadius: '50%',
                      filter: 'blur(120px)',
                      position: 'absolute',
                      top: '-120px',
                      right: '-310px',
                    }}
                  />
                  <div
                    style={{
                      width: '300px',
                      height: '200px',
                      overflow: 'hidden',
                      backgroundColor: isDarkMode ? '#00000040' : '#FFFFFF',
                      rotate: '325deg',
                      borderRadius: '80px',
                      filter: 'blur(120px)',
                      position: 'absolute',
                      bottom: '-190px',
                      left: '-220px',
                    }}
                  />
                  <div
                    style={{
                      width: '400px',
                      height: '400px',
                      overflow: 'hidden',
                      backgroundColor: lightGreyScale[0] + '60',
                      borderRadius: '50%',
                      filter: 'blur(120px)',
                      position: 'absolute',
                      bottom: '-80px',
                      left: '-80px',
                    }}
                  />
                </Box>
                <div
                  className="row"
                  style={{
                    backgroundColor: '#FFFFFF70',
                    border: '1px solid #FFFFFF',
                    borderRadius: '10px',
                    padding: '12px',
                  }}
                >
                  {!isMobile && (
                    <div
                      className="col-2 py-4"
                      style={{
                        backgroundColor: mainColor + 20,
                        borderRadius: '10px',
                      }}
                    >
                      <Button
                        className="middle-content"
                        fullWidth={true}
                        sx={{
                          zIndex: 100,
                          width: '112px',
                          maxWidth: '112px !important',
                          minWidth: '112px !important',
                          height: '24px',
                          background: `linear-gradient(145deg, ${
                            mainColor || '#FF6F91'
                          }, ${darkColor()})`,
                          borderRadius: '5px',
                          padding: '0.4rem',
                          boxShadow: `0px 4px 10px rgba(0, 0, 0, 0.1), 0px -2px 4px rgba(255, 255, 255, 0.2) inset`,
                          color: '#FFFFFF',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            background: `linear-gradient(145deg,${darkColor()}, ${
                              mainColor || '#FF6F91'
                            })`,
                            boxShadow: `0px 6px 15px rgba(0, 0, 0, 0.2), 0px -3px 6px rgba(255, 255, 255, 0.3) inset`,
                            transform: 'scale(1.04)',
                            cursor: 'pointer',
                          },
                          '&:active': {
                            transform: 'scale(1)',
                            boxShadow: `0px 3px 8px rgba(0, 0, 0, 0.2), 0px -2px 3px rgba(255, 255, 255, 0.3) inset`,
                          },
                        }}
                      >
                        <MuiIcons.AddOutlined
                          fontSize="medium"
                          htmlColor={'#FFF'}
                          sx={{
                            marginRight: '5px',
                            marginTop: '-1px',
                            fontSize: '11px',
                          }}
                        />
                        <Typography
                          fontWeight={600}
                          fontSize="9px"
                          color={'#FFF'}
                          variant="body2"
                        >
                          {t('new')}
                        </Typography>
                      </Button>
                      <div className="mt-3 px-1">
                        <div className={'d-flex align-left mb-2'}>
                          <div
                            style={{
                              width: '13px',
                              height: '13px',
                              marginLeft: '0px',
                            }}
                          >
                            {/* {resolveMenuIcon('OPERATIONS')} */}
                          </div>{' '}
                          <div
                            className="hover"
                            style={{ marginLeft: '5px', marginTop: '0px' }}
                          >
                            <Typography
                              fontWeight={600}
                              fontSize="10.5px"
                              color={darkColor()}
                              variant="body2"
                            >
                              {t('OPERATIONS')}
                            </Typography>
                          </div>
                          <div style={{ marginTop: '0px' }}>
                            <MuiIcons.ArrowDropDown
                              sx={{ color: darkColor(), fontSize: '14px' }}
                            />
                          </div>
                        </div>
                        {data?.modules
                          ?.filter((m) => m?.section === 'OPERATIONS')
                          ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                          ?.map((module) => {
                            const IconName =
                              module?.icon || 'DescriptionOutlined';
                            const IconComponent =
                              MuiIcons[IconName] ||
                              MuiIcons.DescriptionOutlined;
                            return (
                              <div className="d-flex middle-content mt-1">
                                <IconComponent
                                  sx={{ fontSize: 9, marginRight: '4px' }}
                                />
                                <Typography fontSize={8} fontWeight={500}>
                                  {module.name_fr ||
                                    module.name_en ||
                                    module.name}
                                </Typography>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  <div
                    style={{ paddingLeft: '20px' }}
                    className="col-md-10 col-12"
                  >
                    <Grid item xs={12} md={6}>
                      <div className="row mb-2 m-1">
                        <div className="col-2">
                          <div>
                            <Typography fontSize={11} fontWeight={600}>
                              {'André Dumais'}
                            </Typography>
                          </div>
                          <div>
                            <Typography
                              fontSize={7}
                              fontWeight={400}
                              className="greyText"
                              sx={{ marginTop: '-2px' }}
                            >
                              {'Contacts'}
                            </Typography>
                          </div>
                        </div>
                        <div className="col-4 d-flex" style={{ gap: 3 }}></div>
                      </div>
                      <Box sx={gridContainerStyle}></Box>
                    </Grid>
                  </div>
                </div>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderNewStructureModal = () => (
    <Dialog
      open={modalStructureNew}
      onClose={() => {
        setModalStructureNew(false);
        setNewStructureName('');
        setNewStructureCollectionField('');
      }}
    >
      <DialogTitle>Add New Structure</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2, minWidth: 300 }}>
          <TextField
            fullWidth
            label="Structure Name"
            value={newStructureName}
            onChange={(e) => setNewStructureName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Collection Field</InputLabel>
            <Select
              value={newStructureCollectionField}
              label="Collection Field"
              onChange={(e) => setNewStructureCollectionField(e.target.value)}
            >
              <MenuItem value="contacts">Contacts</MenuItem>
              <MenuItem value="profiles">Profiles</MenuItem>
              <MenuItem value="cardsinvoiced">Cards Invoiced</MenuItem>
              <MenuItem value="cardsuninvoiced">Cards Uninvoiced</MenuItem>
              <MenuItem value="tasks">Tasks</MenuItem>
              <MenuItem value="storages">Storages</MenuItem>
              <MenuItem value="passes">Passes</MenuItem>
              <MenuItem value="services">Services</MenuItem>
              <MenuItem value="articles">Articles</MenuItem>
              <MenuItem value="grids">Grids</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setModalStructureNew(false);
            setNewStructureName('');
            setNewStructureCollectionField('');
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleAddNewStructure(
              newStructureCollectionField,
              newStructureName
            );
            setModalStructureNew(false);
            setNewStructureName('');
            setNewStructureCollectionField('');
          }}
          disabled={!newStructureName || !newStructureCollectionField}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          '& .MuiDialogContent-root': {
            padding: 0,
          },
        },
      }}
    >
      {renderNewStructureModal()}
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Blueprint Editor</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Tabs
              value={mainTab}
              onChange={(e, newValue) => setMainTab(newValue)}
              sx={{
                minHeight: 'auto',
                '& .MuiTab-root': {
                  minHeight: 'auto',
                  py: 1,
                  px: 2,
                  borderRadius: '20px',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                  },
                },
              }}
              TabIndicatorProps={{ style: { display: 'none' } }}
            >
              <Tab
                icon={<DashboardIcon />}
                label="Structures"
                iconPosition="start"
              />
              <Tab icon={<AppsIcon />} label="Modules" iconPosition="start" />
              <Tab
                icon={<MuiIcons.AccountTree />}
                label="Organization"
                iconPosition="start"
              />
              <Tab
                icon={<MuiIcons.ReceiptLongOutlined />}
                label="Recapitulatif"
                iconPosition="start"
              />
            </Tabs>
            <Box>
              <Tooltip title="Share blueprint">
                <IconButton onClick={handleShare}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>

              <IconButton onClick={onClose} size="large">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', height: '98%', width: '100%' }}>
          {mainTab === 0 && (
            <>
              <Box
                sx={{
                  width: '15%',
                  padding: 2,
                }}
              >
                {renderStructureSelector()}
              </Box>
              <Box sx={{ width: '85%', padding: 1 }}>
                {selectedStructure && (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {showStructureSettings ? renderStructureSettings() : null}
                    {!showStructureSettings && renderStructureTabs()}
                    {!showStructureSettings && renderStructureContent()}
                  </Box>
                )}
              </Box>
            </>
          )}
          {mainTab === 1 && (
            <>
              <Box
                sx={{
                  width: '15%',
                  padding: 2,
                }}
              >
                {renderModuleSelector()}
              </Box>
              <Box sx={{ width: '85%', padding: 1 }}>
                {renderModuleOptions()}
              </Box>
            </>
          )}
          {mainTab === 3 && renderRecapTab()}
          {mainTab === 2 && (
            <Box sx={{ width: '100%', padding: 2 }}>
              <OrganizationChart structures={data.structures} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlueprintEditor;
